import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as Icones from 'lucide-react';
import { construireContexte } from '../../../utils/contexteGabarit';
import { rendre } from '../../../utils/gabarit';

/**
 * L'index des modules — le premier bloc qui LIT LA BASE AU LIEU DE PORTER
 * SON CONTENU.
 *
 * Les vingt et un modules recopiés dans une page deviendraient faux le jour
 * où l'on en ajoute un, en désactive un autre, ou traduit une description.
 * Multiplié par huit langues, le contenu recopié serait ingérable. Ce bloc
 * ne porte donc qu'un GABARIT, appliqué à chaque module.
 *
 * Le gabarit est facultatif : sans lui, le bloc rend nom et résumé, ce qui
 * couvre l'usage courant. Il sert quand une page veut une présentation
 * particulière — « {{ m.nom }} — dès {{ tarif[m.code].prix | monnaie }} ».
 *
 * L'ICÔNE ET LA COULEUR VIENNENT DE LA BASE : la table modules porte déjà
 * `icon` (un nom lucide-react) et `color`. Les redéfinir dans la page ferait
 * diverger la page du menu de l'application.
 */

/** Ce que le bloc rend quand le gabarit ne dit rien. */
const GABARIT_TITRE_DEFAUT = '{{ m.nom }}';
const GABARIT_TEXTE_DEFAUT = '{{ m.resume }}';

/**
 * L'icône du module, par son nom lucide-react.
 *
 * Un nom inconnu ne casse rien : la carte s'affiche sans icône. La table
 * peut porter un nom d'icône qui n'existe plus dans une version ultérieure
 * de la bibliothèque.
 */
function IconeModule({ nom, couleur }) {
    if (!nom) return null;
    const Composant = Icones[nom];
    // Les icônes de lucide-react sont des composants « forwardRef » : des
    // objets, pas des fonctions. Tester `typeof === 'function'` les écartait
    // toutes, et aucune carte n'affichait son icône.
    if (!Composant) return null;

    const style = {};
    if (couleur) style.color = couleur;

    return <Composant className="w-6 h-6 mb-3" style={style} aria-hidden="true" />;
}

const GrilleModulesBloc = ({ bloc, langue = 'fr' }) => {
    const [contexte, setContexte] = useState(null);
    const [chargement, setChargement] = useState(true);

    useEffect(() => {
        let annule = false;

        construireContexte(langue)
            .then((c) => { if (!annule) setContexte(c); })
            .catch(() => { if (!annule) setContexte(null); })
            .finally(() => { if (!annule) setChargement(false); });

        return () => { annule = true; };
    }, [langue]);

    // Pendant le chargement : rien. Une grille vide qui se remplit vaut mieux
    // qu'un pan de page qui saute.
    if (chargement) return null;
    if (!contexte) return null;

    const modules = Array.isArray(contexte.modules) ? contexte.modules : [];
    if (modules.length === 0) return null;

    const gabarit = bloc?.gabarit || {};
    const gabaritTitre = gabarit.titre || GABARIT_TITRE_DEFAUT;
    const gabaritTexte = gabarit.texte || GABARIT_TEXTE_DEFAUT;

    return (
        <section className="mb-10" data-testid="bloc-grilleModules">
            {bloc?.titre && (
                <h2 className="text-xl font-bold text-gray-900 mb-5">{bloc.titre}</h2>
            )}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {modules.map((m) => {
                    // Le contexte de CE module : `m` pour le gabarit, plus les
                    // sources globales pour qu'il puisse viser un tarif.
                    const contexteModule = { ...contexte, m, module: m };

                    const titre = rendre(gabaritTitre, contexteModule, langue);
                    const texte = rendre(gabaritTexte, contexteModule, langue);

                    // Un module sans titre lisible est omis plutôt que de
                    // laisser une carte vide cliquable.
                    if (!titre.trim()) return null;

                    return (
                        <Link
                            key={m.code}
                            to={m.lien}
                            className="block bg-gray-50 rounded-xl p-5 hover:bg-gray-100 transition-colors"
                        >
                            <IconeModule nom={m.icone} couleur={m.couleur} />
                            <h3 className="font-semibold text-gray-900 mb-1">{titre}</h3>
                            {texte && (
                                <p className="text-sm text-gray-600">{texte}</p>
                            )}
                        </Link>
                    );
                })}
            </div>
        </section>
    );
};

export default GrilleModulesBloc;
